// Owner: data-model-forms (plan §9.15, §4.2). The form builder's editor scope: undo, redo, duplicate,
// select all, delete, Esc and ⌘S, plus copy, cut and paste of fields. Verbs that need a selection or a
// history stay in the shortcut sheet (greyed) while they cannot run, so the sheet always shows what
// the builder can do. The clipboard keys stay the browser's own events (`useClipboardSource` /
// `useClipboardTarget`); the commands list them and run them from the command bar.
import {
  type CommandDef,
  defineCommand,
  defineScope,
  editCommands,
  type EditActions,
} from '@dataroom/interaction';
import { saveNowCommand, type SaveNowContext } from '../../editor-kit/commands';
import { t } from '../../lib/i18n';
import { defineAppCommands } from '../../extensions/commands';

export type FormsEditorContext = EditActions &
  SaveNowContext & {
    readOnly: () => boolean;
    copy: () => void;
    cut: () => void;
    paste: () => void;
  };

export const formsEditorScope = defineScope<FormsEditorContext>({
  id: 'forms.editor',
  kind: 'editor',
  title: () => t('s5.form.editor'),
});

const alwaysListed = <C>(command: CommandDef<C>): CommandDef<C> => ({ ...command, listed: 'always' });

const clipboardCommand = (
  name: 'copy' | 'cut' | 'paste',
  shortcut: string,
  when: (c: FormsEditorContext) => boolean,
) =>
  defineCommand<FormsEditorContext>({
    id: `forms.editor.${name}`,
    title: () =>
      t(name === 'copy' ? 's5.form.cmd.copy' : name === 'cut' ? 's5.form.cmd.cut' : 's5.form.cmd.paste'),
    ...(name === 'copy' ? { icon: 'copy' as const } : {}),
    // In the form builder ⌘C copies fields (a record peek's "copy link" belongs to another screen).
    ...(name === 'copy' ? { overrides: ['records.peek.copyLink'] } : {}),
    shortcut,
    scope: formsEditorScope,
    listed: 'always',
    when,
    run: (c, info) => {
      // Typed on the keyboard: the browser's own copy, cut or paste event follows.
      if (info.source === 'keyboard') return false;
      c[name]();
    },
  });

export default defineAppCommands({
  id: 'forms.editor',
  loadTitles: () => import('../../messages-ready/s5-forms'),
  scopes: [formsEditorScope],
  commands: [
    ...editCommands(formsEditorScope, ['undo', 'redo', 'duplicate', 'selectAll', 'delete', 'escape']).map(
      alwaysListed,
    ),
    clipboardCommand('copy', 'mod+c', (c) => (c.hasSelection ? c.hasSelection() : false)),
    clipboardCommand('cut', 'mod+x', (c) => !c.readOnly() && (c.hasSelection ? c.hasSelection() : false)),
    clipboardCommand('paste', 'mod+v', (c) => !c.readOnly()),
    saveNowCommand(formsEditorScope),
  ],
});
